import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Avatar,
  Chip
} from '@mui/material';
import {
  Person,
  Star
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

// Helper function to get department display name
const getDepartmentDisplayName = (department: string, t: any): string => {
  if (!department) return '';
  
  // Try to get the translated department name
  const translatedDept = t(`common.departments.${department}`);
  
  // If the translation key returns the same value, it means the translation wasn't found
  if (translatedDept === `common.departments.${department}`) {
    // Return the original department name if no translation is found
    return department;
  }
  
  return translatedDept;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="xl">
      {/* Welcome Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'primary.main',
                mr: 2
              }}
            >
              <Person sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" gutterBottom>
                {t('dashboard.welcomeBack', { firstName: user.firstName })}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {/* Role display removed - use permission templates instead */}
                {user.department && (
                  <Typography variant="body2" color="text.secondary">
                    • {getDepartmentDisplayName(user.department, t)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Button variant="outlined" onClick={logout}>
            {t('common.logout')}
          </Button>
        </Box>
      </Paper>

      {/* Profile Information */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('dashboard.profileInformation')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 300px' }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.fullName')}
            </Typography>
            <Typography variant="body1">
              {user.firstName} {user.lastName}
            </Typography>
          </Box>
          
          <Box sx={{ flex: '1 1 300px' }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.email')}
            </Typography>
            <Typography variant="body1">
              {user.email}
            </Typography>
          </Box>
          
          {user.position && (
            <Box sx={{ flex: '1 1 300px' }}>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.position')}
              </Typography>
              <Typography variant="body1">
                {user.position}
              </Typography>
            </Box>
          )}
          
          {user.employeeId && (
            <Box sx={{ flex: '1 1 300px' }}>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.employeeId')}
              </Typography>
              <Typography variant="body1">
                {user.employeeId}
              </Typography>
            </Box>
          )}

          {/* Email Verification Status */}
          <Box sx={{ flex: '1 1 300px' }}>
            <Typography variant="body2" color="text.secondary">
              {t('users.emailVerified')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user.approvalStatus === 'approved' ? (
                <>
                  <Chip 
                    label={t('users.verified')} 
                    color="success" 
                    size="small" 
                    icon={<Star sx={{ fontSize: 16 }} />}
                  />
                  {t('users.emailVerified')}
                </>
              ) : (
                <>
                  <Chip 
                    label={t('users.notVerified')} 
                    color="warning" 
                    size="small" 
                    icon={<Star sx={{ fontSize: 16 }} />}
                  />
                  {t('users.notVerified')}
                </>
              )}
            </Box>
          </Box>

          {/* Approval Status */}
          <Box sx={{ flex: '1 1 300px' }}>
            <Typography variant="body2" color="text.secondary">
              {t('users.approvalStatus')}
            </Typography>
            <Typography component="div" variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user.approvalStatus === 'approved' ? (
                <Chip 
                  label={t('users.approved')} 
                  color="success" 
                  size="small" 
                />
              ) : user.approvalStatus === 'pending' ? (
                <Chip 
                  label={t('common.pending')} 
                  color="warning" 
                  size="small" 
                />
              ) : (
                <Chip 
                  label={t('users.rejected')} 
                  color="error" 
                  size="small" 
                />
              )}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Dashboard; 